import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import {
  resolveProductTaxonomyCopy,
  resolveProductTaxonomyTerm,
  type ProductTaxonomyCopyKey,
  type ProductTaxonomyTermReference,
  type ProductTaxonomyVariant,
} from './catalog';
import { resolveProductTaxonomyRoute, type ProductTaxonomyRouteKey } from './routes';

export {
  resolveProductTaxonomyCopy,
  resolveProductTaxonomyTerm,
  type ProductTaxonomyCopyKey,
  type ProductTaxonomyTermFormKey,
  type ProductTaxonomyTermKey,
  type ProductTaxonomyTermReference,
  type ProductTaxonomyVariant,
} from './catalog';
export {
  productTaxonomyCopyCatalog,
  productTaxonomyRouteCatalog,
  productTaxonomySideNavItemIdMap,
  productTaxonomyTermCatalog,
} from './renames';
export {
  normalizeJourneysPathname,
  resolveProductTaxonomyRoute,
  type ProductTaxonomyRouteKey,
  type ProductTaxonomyRouteTemplate,
} from './routes';
export {
  getProductTaxonomySideNavPreferenceIds,
  normalizeProductTaxonomySideNavPreferences,
  type SideNavPreferences,
} from './side-nav-preferences';
export type {
  ProductTaxonomyCopyEntry,
  ProductTaxonomyCopyTermReference,
  ProductTaxonomyRenameDefinition,
  ProductTaxonomyRouteEntry,
  ProductTaxonomyTermEntry,
  ProductTaxonomyTermForms,
} from './types';

export type ProductTaxonomy = {
  variant: ProductTaxonomyVariant;
  t: (key: ProductTaxonomyCopyKey) => string;
  term: (reference: ProductTaxonomyTermReference) => string;
  route: (key: ProductTaxonomyRouteKey) => string;
};

const ProductTaxonomyContext = createContext<ProductTaxonomy | null>(null);

type ProductTaxonomyProviderProps = PropsWithChildren<{
  variant: ProductTaxonomyVariant;
}>;

export function ProductTaxonomyProvider({ children, variant }: ProductTaxonomyProviderProps) {
  const value = useMemo(() => createProductTaxonomy(variant), [variant]);

  return <ProductTaxonomyContext.Provider value={value}>{children}</ProductTaxonomyContext.Provider>;
}

export function useProductTaxonomy(): ProductTaxonomy {
  const context = useContext(ProductTaxonomyContext);
  const isRenameEnabled = useFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRename);
  const variant: ProductTaxonomyVariant = isRenameEnabled ? 'future' : 'current';

  return useMemo(() => context ?? createProductTaxonomy(variant), [context, variant]);
}

export function createProductTaxonomy(variant: ProductTaxonomyVariant): ProductTaxonomy {
  return {
    variant,
    t: (key) => resolveProductTaxonomyCopy(key, variant),
    term: (reference) => resolveProductTaxonomyTerm(reference, variant),
    route: (key) => resolveProductTaxonomyRoute(key, variant),
  };
}
